import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from "react";
import {
  getAllPubPackages,
  PubPackage,
  createPubPackage,
  updatePubPackage,
  deletePubPackage,
  movePubPackageUp,
  movePubPackageDown,
  getFeaturedPubPackages,
} from "../firebase/pubPackageService";
import { Timestamp } from "firebase/firestore";
import { withFirebaseRetry } from "../utils/retryUtils";
import { globalRateLimiter } from "../utils/rateLimiter";

interface PubPackageContextType {
  pubPackages: PubPackage[];
  featuredPubPackages: PubPackage[];
  loading: boolean;
  error: Error | null;
  refreshPubPackages: () => Promise<void>;
  addPubPackage: (
    pubPackage: Omit<PubPackage, "id" | "createdAt" | "updatedAt">
  ) => Promise<string>;
  editPubPackage: (
    id: string,
    pubPackage: Partial<Omit<PubPackage, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  removePubPackage: (id: string) => Promise<void>;
  moveUp: (id: string) => Promise<void>;
  moveDown: (id: string) => Promise<void>;
}

const PubPackageContext = createContext<PubPackageContextType | undefined>(
  undefined
);

export const PubPackageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [pubPackages, setPubPackages] = useState<PubPackage[]>([]);
  const [featuredPubPackages, setFeaturedPubPackages] = useState<PubPackage[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  // Cache duration in milliseconds (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;

  const fetchPubPackages = async (forceRefresh: boolean = false) => {
    // Check if we have cached data and it's still fresh
    const now = Date.now();
    const isCacheValid =
      pubPackages.length > 0 && now - lastFetchTime < CACHE_DURATION;

    if (!forceRefresh && isCacheValid) {
      console.log("Using cached pub packages data");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use retry mechanism for fetching pub packages
      const [packagesData, featuredPackagesData] = await withFirebaseRetry(
        async () => {
          return Promise.all([getAllPubPackages(), getFeaturedPubPackages()]);
        }
      );

      setPubPackages(packagesData);
      setFeaturedPubPackages(featuredPackagesData);
      setLastFetchTime(now);
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to fetch pub packages data")
      );
      console.error("Error fetching pub packages:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPubPackages();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshPubPackages = async () => {
    await fetchPubPackages(true); // Force refresh
  };

  const addPubPackage = async (
    pubPackage: Omit<PubPackage, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    // Rate limiting for pub package operations
    if (!globalRateLimiter.isAllowed("add-pubpackage")) {
      throw new Error(
        "Too many requests. Please wait a moment before trying again."
      );
    }

    const id = await createPubPackage(pubPackage);
    // Instead of refreshing all packages, add the new package to the state
    const newPackage = {
      ...pubPackage,
      id,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    setPubPackages((prevPackages) => [...prevPackages, newPackage]);
    return id;
  };

  const editPubPackage = async (
    id: string,
    pubPackage: Partial<Omit<PubPackage, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    // Rate limiting for pub package updates
    if (!globalRateLimiter.isAllowed(`edit-pubpackage-${id}`)) {
      throw new Error(
        "Too many update requests. Please wait a moment before trying again."
      );
    }

    await updatePubPackage(id, pubPackage);
    // Instead of refreshing all packages, update the specific package in state
    setPubPackages((prevPackages) =>
      prevPackages.map((p) =>
        p.id === id
          ? ({ ...p, ...pubPackage, updatedAt: Timestamp.now() } as PubPackage)
          : p
      )
    );
  };

  const removePubPackage = async (id: string): Promise<void> => {
    await deletePubPackage(id);
    // Instead of refreshing all packages, remove the specific package from state
    setPubPackages((prevPackages) => prevPackages.filter((p) => p.id !== id));
  };

  const moveUp = async (id: string): Promise<void> => {
    await movePubPackageUp(id);
    // For move operations, we need to refresh to get proper ordering
    // But we could optimize this further by handling the reordering in state
    await refreshPubPackages();
  };

  const moveDown = async (id: string): Promise<void> => {
    await movePubPackageDown(id);
    // For move operations, we need to refresh to get proper ordering
    // But we could optimize this further by handling the reordering in state
    await refreshPubPackages();
  };

  const value: PubPackageContextType = {
    pubPackages,
    featuredPubPackages,
    loading,
    error,
    refreshPubPackages,
    addPubPackage,
    editPubPackage,
    removePubPackage,
    moveUp,
    moveDown,
  };

  return (
    <PubPackageContext.Provider value={value}>
      {children}
    </PubPackageContext.Provider>
  );
};

export const usePubPackages = () => {
  const context = useContext(PubPackageContext);
  if (context === undefined) {
    throw new Error("usePubPackages must be used within a PubPackageProvider");
  }
  return context;
};
